/**
 * src/utils/themeUtils.js
 *
 * Pure utility for mapping ThemeColor constants to MUI theme objects.
 * Extracted from containers/App.js to keep the component focused on routing.
 */

import { createTheme } from '@mui/material/styles';
import indigoTheme from '../containers/themes/indigoTheme';
import cyanTheme from '../containers/themes/cyanTheme';
import orangeTheme from '../containers/themes/orangeTheme';
import amberTheme from '../containers/themes/amberTheme';
import pinkTheme from '../containers/themes/pinkTheme';
import blueTheme from '../containers/themes/blueTheme';
import purpleTheme from '../containers/themes/purpleTheme';
import greenTheme from '../containers/themes/greenTheme';
import darkTheme from '../containers/themes/darkTheme';

import {
  AMBER,
  BLUE,
  CYAN,
  DARK_AMBER,
  DARK_BLUE,
  DARK_CYAN,
  DARK_DEEP_ORANGE,
  DARK_DEEP_PURPLE,
  DARK_GREEN,
  DARK_INDIGO,
  DARK_PINK,
  DEEP_ORANGE,
  DEEP_PURPLE,
  GREEN,
  INDIGO,
  PINK,
} from '../constants/ThemeColors';

/**
 * Maps a ThemeColor constant to the corresponding MUI theme object.
 * Falls back to the indigo theme if the color is not recognized.
 *
 * @param {string} themeColor - One of the ThemeColor constants
 * @param {boolean} isDarkTheme - Whether dark mode is active
 * @returns {object} MUI theme created via createTheme()
 */
export function getThemeForColor(themeColor, isDarkTheme) {
  if (isDarkTheme) {
    return createTheme(darkTheme);
  }

  const themeMap = {
    [INDIGO]: indigoTheme,
    [CYAN]: cyanTheme,
    [AMBER]: amberTheme,
    [DEEP_ORANGE]: orangeTheme,
    [PINK]: pinkTheme,
    [BLUE]: blueTheme,
    [DEEP_PURPLE]: purpleTheme,
    [GREEN]: greenTheme,
    // Dark variants use the same base theme as their light counterpart
    [DARK_INDIGO]: indigoTheme,
    [DARK_CYAN]: cyanTheme,
    [DARK_AMBER]: amberTheme,
    [DARK_DEEP_ORANGE]: orangeTheme,
    [DARK_PINK]: pinkTheme,
    [DARK_BLUE]: blueTheme,
    [DARK_DEEP_PURPLE]: purpleTheme,
    [DARK_GREEN]: greenTheme,
  };

  const baseTheme = themeMap[themeColor] ?? indigoTheme;
  return createTheme(baseTheme);
}
